import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import ScheduleIcon from '@mui/icons-material/Schedule';
import moment from 'moment';
import {
  deleteHallBookingVendorAssignment,
  getHallBookingDetail,
} from '../modules/hall/api';
import HallAddExtraChargesDrawer from '../modules/hall/components/HallAddExtraChargesDrawer';
import HallAddPaymentDrawer from '../modules/hall/components/HallAddPaymentDrawer';
import HallAddVendorDrawer from '../modules/hall/components/HallAddVendorDrawer';
import HallAddVendorPaymentDrawer from '../modules/hall/components/HallAddVendorPaymentDrawer';
import HallAuditDrawer from '../modules/hall/components/HallAuditDrawer';
import HallEventBillingSection from '../modules/hall/components/HallEventBillingSection';
import HallEventDocumentsPreviewPanel from '../modules/hall/components/HallEventDocumentsPreviewPanel';
import HallModuleNav from '../modules/hall/components/HallModuleNav';
import {
  getHotelCommunicationSettings,
  isCommEventEnabled,
} from '../services/api';
import {
  formatHallCurrency,
  paymentStatusMeta,
  resolveHallPaymentSummary,
} from '../modules/hall/utils/hallFinancials';
import { parseHallEventBookingNotes } from '../modules/hall/utils/hallEventForm';
import { getHallFullDayAmount, getHallHourlyAmount } from '../modules/hall/utils/normalizeHall';
import {
  DASHBOARD_ACCENT,
  getDashboardDarkSurfaceTokens,
  getDashboardPageShellSx,
} from '../utils/dashboardSurfaceSx';

const HALL_ACCENT = '#4ce1b6';
const VENDOR_EDIT_LOCKED_STATUSES = new Set(['cancelled', 'completed']);

function formatCurrency(value) {
  return formatHallCurrency(value);
}

function formatDate(value) {
  if (!value) return '—';
  return moment(value).format('DD MMM YYYY');
}

function getBookingDurationParts(booking) {
  const start = moment(
    `${booking?.event_date || ''} ${String(booking?.start_time || '').slice(0, 5)}`,
    'YYYY-MM-DD HH:mm',
  );
  const end = moment(
    `${booking?.end_date || booking?.event_date || ''} ${String(booking?.end_time || '').slice(0, 5)}`,
    'YYYY-MM-DD HH:mm',
  );
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
    return { hours: 0, minutes: 0, days: 0, totalMinutes: 0 };
  }
  const totalMinutes = end.diff(start, 'minutes');
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    days: Math.max(1, moment(booking?.end_date || booking?.event_date).diff(moment(booking?.event_date), 'days')),
    totalMinutes,
  };
}

function formatDurationLabel(hours, minutes) {
  if (hours <= 0 && minutes <= 0) return '';
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
}

function formatBookingTypeLabel(bookingType, booking) {
  const duration = getBookingDurationParts(booking);
  const durationText = formatDurationLabel(duration.hours, duration.minutes);
  if (bookingType === 'day') {
    const dayCount = duration.days || 1;
    const dayLabel = `${dayCount} day${dayCount === 1 ? '' : 's'}`;
    return durationText ? `Full day · ${dayLabel} (${durationText})` : `Full day · ${dayLabel}`;
  }
  if (bookingType === 'hourly') {
    return durationText ? `Hourly · ${durationText}` : 'Hourly';
  }
  return durationText || '—';
}

function formatCompactDateTime(value) {
  if (!value) return null;
  return moment(value).format('DD MMM, hh:mm A');
}

function humanizeToken(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  return normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getStatusChipColor(status, isDark) {
  const key = String(status || '').toLowerCase();
  if (key === 'confirmed' || key === 'completed') {
    return { color: isDark ? '#bbf7d0' : '#166534', bg: alpha('#22c55e', isDark ? 0.22 : 0.14) };
  }
  if (key === 'tentative') {
    return { color: isDark ? '#bfdbfe' : '#1d4ed8', bg: alpha('#3b82f6', isDark ? 0.22 : 0.14) };
  }
  if (key === 'cancelled') {
    return { color: isDark ? '#fecaca' : '#991b1b', bg: alpha('#ef4444', isDark ? 0.22 : 0.14) };
  }
  return { color: isDark ? '#cbd5e1' : '#475569', bg: alpha('#64748b', isDark ? 0.2 : 0.12) };
}

function getVendorRowId(vendor) {
  const rawId = vendor?.vendor_id ?? vendor?.id ?? null;
  const numericId = Number(rawId);
  return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
}

function normalizeVendorRow(vendor) {
  const vendorId = getVendorRowId(vendor);
  return {
    id: vendorId,
    assignmentId: vendor?.vendor_id != null ? vendor?.id ?? null : null,
    name: String(vendor?.name || '').trim(),
    vendor_type: String(vendor?.vendor_type || '').trim(),
    vendor_type_label: String(vendor?.vendor_type_label || '').trim(),
    phone: String(vendor?.phone || '').trim(),
    email: String(vendor?.email || '').trim(),
    services: String(vendor?.services || '').trim(),
    is_active: vendor?.is_active !== false,
    assigned_at: vendor?.assigned_at || '',
    notes: String(vendor?.notes || '').trim(),
  };
}

function mergeVendorRows(...groups) {
  const vendorMap = new Map();
  groups.flat().forEach((vendor) => {
    const normalized = normalizeVendorRow(vendor);
    if (!normalized.id) return;
    const existing = vendorMap.get(normalized.id);
    vendorMap.set(normalized.id, {
      ...existing,
      ...normalized,
      assigned_at: existing?.assigned_at || normalized.assigned_at,
      notes: existing?.notes || normalized.notes,
    });
  });
  return Array.from(vendorMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function getVendorAssignmentKey(vendors) {
  return vendors
    .map((vendor) => {
      const id = getVendorRowId(vendor);
      if (!id) return null;
      return `${id}:${String(vendor?.notes || '').trim()}`;
    })
    .filter(Boolean)
    .sort()
    .join('|');
}

function SummaryRow({ icon: Icon, label, value, iconColor, isDark, onValueClick, valueLinkColor }) {
  const isClickable = Boolean(onValueClick && value);
  const linkColor = valueLinkColor || iconColor || HALL_ACCENT;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.1 }}>
      <Icon sx={{ fontSize: 18, color: iconColor, mt: 0.15 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, display: 'block' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          onClick={isClickable ? onValueClick : undefined}
          sx={{
            color: isClickable ? linkColor : (isDark ? '#e2e8f0' : '#0f172a'),
            fontWeight: isClickable ? 700 : 600,
            wordBreak: 'break-word',
            cursor: isClickable ? 'pointer' : 'default',
            textDecoration: isClickable ? 'underline' : 'none',
            textDecorationColor: isClickable ? alpha(linkColor, 0.55) : undefined,
            textUnderlineOffset: '0.2em',
            '&:hover': isClickable ? {
              color: linkColor,
              textDecorationColor: linkColor,
            } : undefined,
          }}
        >
          {value || '—'}
        </Typography>
      </Box>
    </Box>
  );
}

function vendorInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return 'V';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function VendorManagementCards({
  vendors,
  isDark,
  accent,
  locked,
  onAddPayment,
  onEdit,
  onRemove,
  removingId,
}) {
  if (!vendors.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2.5,
          border: `1px dashed ${isDark ? alpha('#64748b', 0.5) : alpha('#94a3b8', 0.55)}`,
          background: isDark
            ? `linear-gradient(145deg, ${alpha('#0f172a', 0.55)}, ${alpha(accent, 0.06)})`
            : `linear-gradient(145deg, #ffffff, ${alpha(accent, 0.06)})`,
        }}
      >
        <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
          No vendors assigned to this event yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={1.1}>
      {vendors.map((vendor) => {
        const contract = Number(vendor.contract_amount || 0) || 0;
        const paid = Number(vendor.paid_amount || 0) || 0;
        const due = Number(vendor.due_amount != null ? vendor.due_amount : Math.max(0, contract - paid)) || 0;
        const included = vendor.include_in_hall !== false;
        const payments = Array.isArray(vendor.payments) ? vendor.payments : [];
        const paidRatio = contract > 0 ? Math.min(100, Math.round((paid / contract) * 100)) : (paid > 0 ? 100 : 0);
        const taskText = vendor.task?.trim() || 'No task assigned';
        const typeLabel = vendor.vendor_type_label || humanizeToken(vendor.vendor_type) || 'Vendor';

        return (
          <Paper
            key={vendor.id}
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 2.5,
              border: `1px solid ${isDark ? alpha('#64748b', 0.32) : alpha('#cbd5e1', 0.7)}`,
              background: isDark
                ? `linear-gradient(155deg, ${alpha('#0f172a', 0.92)} 0%, ${alpha('#122033', 0.9)} 100%)`
                : `linear-gradient(155deg, #ffffff 0%, ${alpha(accent, 0.05)} 100%)`,
              boxShadow: isDark ? 'none' : `0 8px 22px ${alpha('#0f172a', 0.04)}`,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                background: `linear-gradient(180deg, ${accent}, ${alpha(accent, 0.35)})`,
              },
            }}
          >
            <Box sx={{ px: 1.5, pt: 1.25, pb: 1.15, pl: 1.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, minWidth: 0, flex: 1 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: '0.74rem',
                      fontWeight: 800,
                      color: '#04120e',
                      bgcolor: alpha(accent, isDark ? 0.85 : 0.95),
                      boxShadow: `0 0 0 3px ${alpha(accent, isDark ? 0.18 : 0.16)}`,
                      flexShrink: 0,
                    }}
                  >
                    {vendorInitials(vendor.name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: isDark ? '#f8fafc' : '#0f172a',
                        fontWeight: 800,
                        fontSize: '0.95rem',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {vendor.name || 'Unnamed vendor'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, mt: 0.15, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                        {typeLabel}
                      </Typography>
                      <Chip
                        size="small"
                        label={included ? 'In hall' : 'Excluded'}
                        sx={{
                          height: 18,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          flexShrink: 0,
                          color: included
                            ? (isDark ? '#bbf7d0' : '#166534')
                            : (isDark ? '#fde68a' : '#92400e'),
                          backgroundColor: included
                            ? alpha('#22c55e', isDark ? 0.2 : 0.12)
                            : alpha('#f59e0b', isDark ? 0.2 : 0.12),
                          '& .MuiChip-label': { px: 0.7 },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {!locked ? (
                  <Stack direction="row" spacing={0.6} alignItems="center" sx={{ flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PaymentsIcon sx={{ fontSize: 16 }} />}
                      disabled={due <= 0.009}
                      onClick={() => onAddPayment?.(vendor)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        fontSize: '0.78rem',
                        px: 1.1,
                        py: 0.3,
                        minHeight: 30,
                        borderRadius: 1.5,
                        backgroundColor: accent,
                        color: '#04120e',
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: accent, opacity: 0.92, boxShadow: 'none' },
                      }}
                    >
                      Pay
                    </Button>
                    <Tooltip title="Edit assignment">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onEdit?.(vendor)}
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(accent, 0.45)}`,
                            color: accent,
                            bgcolor: alpha(accent, isDark ? 0.08 : 0.1),
                          }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove vendor">
                      <span>
                        <IconButton
                          size="small"
                          disabled={removingId === vendor.id}
                          onClick={() => onRemove?.(vendor)}
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: 1.5,
                            border: `1px solid ${isDark ? alpha('#f87171', 0.35) : alpha('#ef4444', 0.3)}`,
                            color: isDark ? '#fca5a5' : '#dc2626',
                            bgcolor: isDark ? alpha('#ef4444', 0.08) : alpha('#ef4444', 0.06),
                          }}
                        >
                          {removingId === vendor.id
                            ? <CircularProgress size={14} color="inherit" />
                            : <DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ) : null}
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 0.65,
                  mb: 0.85,
                }}
              >
                {[
                  { label: 'Total', value: formatCurrency(contract), color: isDark ? '#e2e8f0' : '#0f172a' },
                  { label: 'Paid', value: formatCurrency(paid), color: isDark ? '#86efac' : '#15803d' },
                  {
                    label: 'Due',
                    value: formatCurrency(due),
                    color: due > 0 ? (isDark ? '#fbbf24' : '#c2410c') : (isDark ? '#86efac' : '#15803d'),
                  },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      px: 0.85,
                      py: 0.55,
                      borderRadius: 1.5,
                      backgroundColor: isDark ? alpha('#94a3b8', 0.08) : alpha('#0f172a', 0.03),
                      border: `1px solid ${isDark ? alpha('#94a3b8', 0.12) : alpha('#0f172a', 0.05)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: isDark ? '#94a3b8' : '#64748b',
                        fontWeight: 700,
                        fontSize: '0.62rem',
                        letterSpacing: 0.3,
                        textTransform: 'uppercase',
                        lineHeight: 1.1,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: item.color, fontSize: '0.84rem', lineHeight: 1.25 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mb: 0.9 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.35 }}>
                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
                    Settlement
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#475569', fontWeight: 800 }}>
                    {paidRatio}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={paidRatio}
                  sx={{
                    height: 5,
                    borderRadius: 99,
                    backgroundColor: isDark ? alpha('#94a3b8', 0.18) : alpha('#0f172a', 0.08),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 99,
                      backgroundColor: due > 0 ? '#f59e0b' : accent,
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.7,
                  mb: payments.length || vendor.notes ? 0.85 : 0.95,
                }}
              >
                <AssignmentOutlinedIcon
                  sx={{ fontSize: 15, mt: '2px', color: isDark ? alpha(accent, 0.9) : alpha('#0f766e', 0.9) }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? '#e2e8f0' : '#334155',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    lineHeight: 1.35,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {taskText}
                </Typography>
              </Box>

              {vendor.notes ? (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: isDark ? '#94a3b8' : '#64748b',
                    mb: 0.85,
                    whiteSpace: 'pre-line',
                    lineHeight: 1.35,
                  }}
                >
                  {vendor.notes}
                </Typography>
              ) : null}

              {payments.length ? (
                <Box
                  sx={{
                    mb: 0.95,
                    borderRadius: 1.75,
                    border: `1px solid ${isDark ? alpha('#94a3b8', 0.14) : alpha('#0f172a', 0.06)}`,
                    backgroundColor: isDark ? alpha('#020617', 0.35) : alpha('#f8fafc', 0.9),
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      px: 1,
                      py: 0.45,
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom: `1px solid ${isDark ? alpha('#94a3b8', 0.12) : alpha('#0f172a', 0.05)}`,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 800 }}>
                      Payouts
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
                      {payments.length}
                    </Typography>
                  </Box>
                  <Stack divider={<Divider sx={{ borderColor: isDark ? alpha('#94a3b8', 0.1) : alpha('#0f172a', 0.05) }} />}>
                    {payments.map((payment) => (
                      <Box
                        key={payment.id}
                        sx={{
                          px: 1,
                          py: 0.55,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: isDark ? '#f1f5f9' : '#0f172a' }}>
                            {formatCurrency(payment.amount)}
                            <Box
                              component="span"
                              sx={{
                                ml: 0.6,
                                fontWeight: 700,
                                color: isDark ? '#94a3b8' : '#64748b',
                                fontSize: '0.7rem',
                              }}
                            >
                              {payment.payment_type_label
                                || String(payment.payment_type || '').replace(/_/g, ' ')
                                || 'Payout'}
                            </Box>
                          </Typography>
                          <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontWeight: 600 }}>
                            {String(payment.mode || payment.payment_method || 'cash').replace(/_/g, ' ')}
                            {payment.paid_at || payment.processed_at
                              ? ` · ${formatCompactDateTime(payment.paid_at || payment.processed_at)}`
                              : ''}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </Box>
          </Paper>
        );
      })}
    </Stack>
  );
}

export default function HallEventDetailPage() {
  const { bookingId } = useParams();
  const normalizedBookingId = useMemo(() => {
    const parsed = Number(bookingId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [bookingId]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [vendorDrawerOpen, setVendorDrawerOpen] = useState(false);
  const [editingVendorAssignment, setEditingVendorAssignment] = useState(null);
  const [vendorPaymentDrawerOpen, setVendorPaymentDrawerOpen] = useState(false);
  const [payingVendorAssignment, setPayingVendorAssignment] = useState(null);
  const [removingVendorId, setRemovingVendorId] = useState(null);
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [auditRefreshLoading, setAuditRefreshLoading] = useState(false);
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
  const [extraChargesDrawerOpen, setExtraChargesDrawerOpen] = useState(false);
  const [editingExtraCharge, setEditingExtraCharge] = useState(null);
  const [docLoading, setDocLoading] = useState(null);
  const [commSettings, setCommSettings] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const showNotice = useCallback((severity, message) => {
    setSnackbar({ open: true, severity, message });
  }, []);

  const canSendInvoiceEmail = useMemo(
    () => isCommEventEnabled(commSettings, 'hall_event', 'email', 'guest', 'manual'),
    [commSettings],
  );
  const canSendInvoiceWhatsApp = useMemo(
    () => isCommEventEnabled(commSettings, 'hall_event', 'whatsapp', 'guest', 'manual'),
    [commSettings],
  );

  useEffect(() => {
    let cancelled = false;
    getHotelCommunicationSettings()
      .then((data) => {
        if (!cancelled) setCommSettings(data);
      })
      .catch(() => {
        if (!cancelled) setCommSettings(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadDetail = useCallback(async ({ showPageLoader = true } = {}) => {
    if (!normalizedBookingId) {
      setError('Invalid event ID');
      if (showPageLoader) {
        setLoading(false);
      }
      return;
    }
    if (showPageLoader) {
      setLoading(true);
    }
    try {
      const data = await getHallBookingDetail(normalizedBookingId);
      setPayload(data);
      setError(null);
    } catch (err) {
      const message = err?.userMessage || err?.message || 'Failed to load event details';
      if (showPageLoader) {
        setError(message);
      } else {
        showNotice('error', message);
      }
    } finally {
      if (showPageLoader) {
        setLoading(false);
      }
    }
  }, [normalizedBookingId, showNotice]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const booking = payload?.booking || null;
  const hall = payload?.hall || null;
  const availableVendors = useMemo(
    () => (Array.isArray(payload?.available_vendors) ? payload.available_vendors : []),
    [payload?.available_vendors]
  );
  const assignedVendors = useMemo(
    () => (Array.isArray(payload?.assigned_vendors) ? payload.assigned_vendors : []),
    [payload?.assigned_vendors]
  );
  const auditEvents = useMemo(() => {
    const activity = Array.isArray(payload?.activity) ? payload.activity : null;
    if (activity?.length) {
      return [...activity].sort((left, right) => {
        const leftTime = new Date(left?.created_at || 0).getTime();
        const rightTime = new Date(right?.created_at || 0).getTime();
        return leftTime - rightTime;
      });
    }
    const events = Array.isArray(payload?.audit_logs) ? payload.audit_logs : [];
    return [...events]
      .map((event) => ({
        id: event?.id,
        type: event?.event_type || event?.type,
        title: event?.title,
        description: event?.description,
        actor: event?.actor,
        created_at: event?.created_at,
        payload: event?.payload || {},
      }))
      .sort((left, right) => {
        const leftTime = new Date(left?.created_at || 0).getTime();
        const rightTime = new Date(right?.created_at || 0).getTime();
        return leftTime - rightTime;
      });
  }, [payload?.activity, payload?.audit_logs]);



  const eventId = booking?.event_id || booking?.booking_code || '—';
  const customerGuestId = booking?.customer_guest_id || null;
  const statusPalette = getStatusChipColor(booking?.status, isDark);
  const pageShellSx = getDashboardPageShellSx(isDark ? 'dark' : 'light');
  const surfaceTokens = getDashboardDarkSurfaceTokens(isDark);
  const accent = DASHBOARD_ACCENT;
  const paymentSummary = useMemo(
    () => resolveHallPaymentSummary(payload, booking),
    [payload, booking],
  );
  const paymentMeta = paymentStatusMeta(paymentSummary.payment_status, isDark);
  const parsedNotes = useMemo(
    () => parseHallEventBookingNotes(booking?.notes),
    [booking?.notes],
  );
  const hallImageUrl = hall?.hall_image_url
    || (Array.isArray(hall?.hall_images) ? hall.hall_images[0]?.url : '')
    || '';
  const hallHourlyRate = getHallHourlyAmount(hall);
  const hallFullDayRate = getHallFullDayAmount(hall);
  const scheduleEndDate = booking?.end_date || booking?.event_date;
  const timeRangeLabel = booking?.start_time && booking?.end_time
    ? `${String(booking.start_time).slice(0, 5)} – ${String(booking.end_time).slice(0, 5)}`
    : '—';
  const scheduleLabel = scheduleEndDate && scheduleEndDate !== booking?.event_date
    ? `${formatDate(booking.event_date)} → ${formatDate(scheduleEndDate)} · ${timeRangeLabel}`
    : `${formatDate(booking?.event_date)} · ${timeRangeLabel}`;
  const bookingTypeLabel = formatBookingTypeLabel(parsedNotes.booking_type, booking);

  const modernPanelCardSx = {
    mb: 2,
    borderRadius: 2,
    border: `1px solid ${surfaceTokens.surfaceBorder}`,
    background: isDark ? surfaceTokens.surfaceGradient : '#ffffff',
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    overflow: 'hidden',
  };

  const isVendorEditingLocked = VENDOR_EDIT_LOCKED_STATUSES.has(
    String(booking?.status || '').trim().toLowerCase()
  );

  const handleRefreshAuditLogs = useCallback(async () => {
    setAuditRefreshLoading(true);
    try {
      await loadDetail({ showPageLoader: false });
    } finally {
      setAuditRefreshLoading(false);
    }
  }, [loadDetail]);

  const handleVendorPayloadUpdated = useCallback((nextPayload) => {
    if (nextPayload?.booking) {
      setPayload(nextPayload);
      const vendors = Array.isArray(nextPayload.assigned_vendors) ? nextPayload.assigned_vendors : [];
      setEditingVendorAssignment((current) => {
        if (!current?.id) return current;
        return vendors.find((row) => Number(row.id) === Number(current.id)) || current;
      });
      setPayingVendorAssignment((current) => {
        if (!current?.id) return current;
        return vendors.find((row) => Number(row.id) === Number(current.id)) || current;
      });
      return;
    }
    loadDetail({ showPageLoader: false });
  }, [loadDetail]);

  const handleRemoveVendor = async (vendor) => {
    if (!normalizedBookingId || !vendor?.id || isVendorEditingLocked) return;
    setRemovingVendorId(vendor.id);
    try {
      const nextPayload = await deleteHallBookingVendorAssignment(normalizedBookingId, vendor.id);
      setPayload(nextPayload);
      showNotice('success', `${vendor.name || 'Vendor'} removed from this event.`);
    } catch (err) {
      showNotice('error', err?.userMessage || err?.message || 'Failed to remove vendor');
    } finally {
      setRemovingVendorId(null);
    }
  };

  const handleFinancialUpdated = useCallback((nextPayload) => {
    if (nextPayload?.booking) {
      setPayload(nextPayload);
      return;
    }
    loadDetail({ showPageLoader: false });
  }, [loadDetail]);

  if (loading) {
    return (
      <Box sx={pageShellSx}>
        <Box sx={{ mb: 2.5 }}>
          <HallModuleNav />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
          <CircularProgress sx={{ color: accent }} />
        </Box>
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Box sx={pageShellSx}>
        <Box sx={{ mb: 2.5 }}>
          <HallModuleNav />
        </Box>
        <Paper sx={{ ...modernPanelCardSx, p: 3, textAlign: 'center' }}>
          <Typography color="error">{error || 'Event not found'}</Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate('/hall/dashboard/')}>Back to Hall Dashboard</Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ ...pageShellSx, width: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ mb: 2.5 }}>
        <HallModuleNav />
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...modernPanelCardSx,
          mb: 2.25,
          position: 'relative',
          overflow: 'hidden',
          background: isDark
            ? `linear-gradient(135deg, ${alpha('#0b1220', 0.98)} 0%, ${alpha('#10261f', 0.96)} 48%, ${alpha('#0f172a', 0.98)} 100%)`
            : `linear-gradient(135deg, #f7fffc 0%, #eef8f4 42%, #f8fafc 100%)`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 280,
            height: 280,
            borderRadius: '50%',
            top: -120,
            right: -80,
            background: `radial-gradient(circle, ${alpha(HALL_ACCENT, isDark ? 0.28 : 0.22)} 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 180,
            height: 180,
            borderRadius: '50%',
            bottom: -90,
            left: -40,
            background: `radial-gradient(circle, ${alpha('#5b9cf5', isDark ? 0.22 : 0.14)} 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: isDark ? 0.08 : 0.05,
            backgroundImage: `linear-gradient(${alpha(HALL_ACCENT, 0.55)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(HALL_ACCENT, 0.55)} 1px, transparent 1px)`,
            backgroundSize: '28px 28px',
            maskImage: 'linear-gradient(180deg, black 0%, transparent 85%)',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ position: 'relative', p: { xs: 1.75, sm: 2, md: 2.25 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 1.75, md: 2 }}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
            justifyContent="space-between"
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Chip
                  size="small"
                  icon={<EventIcon sx={{ fontSize: '0.95rem !important' }} />}
                  label={eventId}
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    color: isDark ? '#04120e' : '#064e3b',
                    bgcolor: HALL_ACCENT,
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
                <Chip
                  size="small"
                  label={booking.status}
                  sx={{ fontWeight: 700, textTransform: 'capitalize', color: statusPalette.color, bgcolor: statusPalette.bg }}
                />
                <Chip
                  size="small"
                  label={paymentMeta.label}
                  sx={{ fontWeight: 700, color: paymentMeta.color, bgcolor: paymentMeta.bg }}
                />
              </Stack>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.15,
                  color: isDark ? '#f8fafc' : '#0f172a',
                  mb: 0.5,
                  fontSize: { xs: '1.45rem', sm: '1.7rem', md: '1.85rem' },
                }}
              >
                {booking.event_name}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: isDark ? '#94a3b8' : '#475569',
                  fontWeight: 600,
                  mb: 1.25,
                  maxWidth: 720,
                }}
              >
                {scheduleLabel}
                {parsedNotes.booking_type
                  ? ` · ${parsedNotes.booking_type === 'day' ? 'Full day' : 'Hourly'}`
                  : ''}
                {parsedNotes.event_type ? ` · ${humanizeToken(parsedNotes.event_type)}` : ''}
              </Typography>

              <Stack direction="row" spacing={0.85} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                {[
                  {
                    icon: GroupsIcon,
                    label: `${booking.guests || 0} guests`,
                    color: '#38bdf8',
                  },
                  {
                    icon: LocalShippingIcon,
                    label: `${assignedVendors.length} vendor${assignedVendors.length === 1 ? '' : 's'}`,
                    color: accent,
                  },
                  {
                    icon: EventIcon,
                    label: bookingTypeLabel !== '—'
                      ? bookingTypeLabel
                      : (humanizeToken(parsedNotes.event_type) || 'Event'),
                    color: '#5b9cf5',
                  },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <Chip
                      key={item.label}
                      size="small"
                      icon={<Icon sx={{ fontSize: '0.95rem !important' }} />}
                      label={item.label}
                      sx={{
                        height: 28,
                        fontWeight: 700,
                        color: isDark ? '#e2e8f0' : '#1e293b',
                        bgcolor: alpha(item.color, isDark ? 0.16 : 0.12),
                        border: `1px solid ${alpha(item.color, isDark ? 0.28 : 0.22)}`,
                        '& .MuiChip-icon': { color: item.color },
                      }}
                    />
                  );
                })}
              </Stack>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<PaymentsIcon />}
                  onClick={() => setPaymentDrawerOpen(true)}
                  disabled={String(booking.status || '').toLowerCase() === 'cancelled'}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    px: 1.8,
                    py: 0.85,
                    borderRadius: 2,
                    backgroundColor: HALL_ACCENT,
                    color: '#04120e',
                    boxShadow: `0 8px 18px ${alpha(HALL_ACCENT, 0.3)}`,
                    '&:hover': { backgroundColor: HALL_ACCENT, opacity: 0.94 },
                  }}
                >
                  Collect payment
                </Button>
              </Stack>
            </Box>

            <Box
              sx={{
                width: { xs: '100%', md: 380 },
                flexShrink: 0,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                minHeight: { sm: 210 },
                overflow: 'hidden',
                borderRadius: 3,
                border: `1px solid ${isDark ? alpha(HALL_ACCENT, 0.34) : alpha(HALL_ACCENT, 0.36)}`,
                background: isDark
                  ? `linear-gradient(145deg, ${alpha('#0f172a', 0.95)} 0%, ${alpha('#10261f', 0.92)} 100%)`
                  : `linear-gradient(145deg, #ffffff 0%, ${alpha('#ecfdf5', 0.96)} 100%)`,
                boxShadow: isDark
                  ? `0 16px 32px ${alpha('#000', 0.32)}`
                  : `0 14px 30px ${alpha('#0f172a', 0.08)}`,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: '100%', sm: 148, md: 168 },
                  minHeight: { xs: 148, sm: 'auto' },
                  flexShrink: 0,
                  overflow: 'hidden',
                  background: isDark
                    ? `linear-gradient(160deg, ${alpha(HALL_ACCENT, 0.28)} 0%, ${alpha('#0f172a', 0.9)} 100%)`
                    : `linear-gradient(160deg, ${alpha(HALL_ACCENT, 0.35)} 0%, ${alpha('#064e3b', 0.55)} 100%)`,
                }}
              >
                {hallImageUrl ? (
                  <Box
                    component="img"
                    src={hallImageUrl}
                    alt={hall?.name || booking.hall_name || 'Hall'}
                    sx={{
                      width: '100%',
                      height: '100%',
                      minHeight: { xs: 148, sm: '100%' },
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      minHeight: { xs: 148, sm: 210 },
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: alpha('#fff', isDark ? 0.12 : 0.22),
                        color: isDark ? HALL_ACCENT : '#ecfdf5',
                        border: `1px solid ${alpha('#fff', 0.28)}`,
                      }}
                    >
                      <MapsHomeWorkIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                  </Box>
                )}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: {
                      xs: `linear-gradient(180deg, transparent 45%, ${alpha('#04120e', 0.55)} 100%)`,
                      sm: `linear-gradient(90deg, transparent 55%, ${alpha('#04120e', isDark ? 0.35 : 0.18)} 100%)`,
                    },
                    pointerEvents: 'none',
                  }}
                />
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  flex: 1,
                  minWidth: 0,
                  p: { xs: 1.4, sm: 1.5 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.1,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? HALL_ACCENT : '#0f766e',
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Venue
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: isDark ? '#f8fafc' : '#0f172a',
                        fontSize: { xs: '1.05rem', sm: '1.12rem' },
                        lineHeight: 1.25,
                        mt: 0.2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {hall?.name || booking.hall_name || 'Hall'}
                    </Typography>
                    {(hall?.hall_code || hall?.hall_type || hall?.business_type_label) ? (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.25,
                          color: isDark ? '#94a3b8' : '#64748b',
                          fontWeight: 600,
                        }}
                      >
                        {[hall?.hall_code, hall?.hall_type || hall?.business_type_label].filter(Boolean).join(' · ')}
                      </Typography>
                    ) : null}
                  </Box>
                  <Chip
                    size="small"
                    icon={<FactCheckIcon sx={{ fontSize: '15px !important' }} />}
                    label={`Audit${auditEvents.length ? ` · ${auditEvents.length}` : ''}`}
                    onClick={() => setAuditDrawerOpen(true)}
                    sx={{
                      height: 30,
                      flexShrink: 0,
                      fontWeight: 800,
                      cursor: 'pointer',
                      color: isDark ? '#04120e' : '#064e3b',
                      bgcolor: HALL_ACCENT,
                      border: `1px solid ${alpha(HALL_ACCENT, 0.55)}`,
                      boxShadow: `0 6px 14px ${alpha(HALL_ACCENT, 0.28)}`,
                      '& .MuiChip-icon': { color: 'inherit' },
                      '&:hover': {
                        bgcolor: HALL_ACCENT,
                        opacity: 0.92,
                      },
                    }}
                  />
                </Stack>

                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ flex: 1 }}>
                  {[
                    {
                      icon: MeetingRoomIcon,
                      label: hall?.capacity ? `Cap. ${hall.capacity}` : 'Cap. —',
                      color: '#a78bfa',
                    },
                    {
                      icon: GroupsIcon,
                      label: `${booking.guests || 0} booked`,
                      color: '#38bdf8',
                    },
                    ...(hallHourlyRate || hallFullDayRate
                      ? [{
                        icon: AttachMoneyIcon,
                        label: [
                          hallHourlyRate ? `${formatCurrency(hallHourlyRate)}/hr` : null,
                          hallFullDayRate ? `${formatCurrency(hallFullDayRate)}/day` : null,
                        ].filter(Boolean).join(' · '),
                        color: HALL_ACCENT,
                      }]
                      : []),
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <Chip
                        key={item.label}
                        size="small"
                        icon={<Icon sx={{ fontSize: '0.9rem !important' }} />}
                        label={item.label}
                        sx={{
                          height: 26,
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          bgcolor: alpha(item.color, isDark ? 0.18 : 0.12),
                          border: `1px solid ${alpha(item.color, isDark ? 0.3 : 0.22)}`,
                          '& .MuiChip-icon': { color: item.color },
                        }}
                      />
                    );
                  })}
                </Stack>

                {booking.hall ? (
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    startIcon={<MapsHomeWorkIcon />}
                    onClick={() => navigate(`/hall/detail/${booking.hall}/`)}
                    sx={{
                      mt: 'auto',
                      textTransform: 'none',
                      fontWeight: 800,
                      borderRadius: 2,
                      py: 0.75,
                      color: '#04120e',
                      backgroundColor: HALL_ACCENT,
                      boxShadow: `0 8px 18px ${alpha(HALL_ACCENT, 0.28)}`,
                      '&:hover': {
                        backgroundColor: HALL_ACCENT,
                        opacity: 0.94,
                      },
                    }}
                  >
                    Open hall profile
                  </Button>
                ) : null}
              </Box>
            </Box>
          </Stack>

          <Grid container spacing={1} sx={{ mt: 1.75 }}>
            {[
              {
                icon: CalendarTodayIcon,
                label: 'Start date',
                value: formatDate(booking.event_date),
                color: HALL_ACCENT,
              },
              {
                icon: CalendarTodayIcon,
                label: 'End date',
                value: formatDate(scheduleEndDate || booking.event_date),
                color: '#34d399',
              },
              {
                icon: ScheduleIcon,
                label: 'Time',
                value: timeRangeLabel,
                color: '#eab308',
              },
              {
                icon: EventIcon,
                label: 'Booking type',
                value: bookingTypeLabel !== '—'
                  ? bookingTypeLabel
                  : (humanizeToken(parsedNotes.event_type) || 'Event'),
                color: '#5b9cf5',
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Grid item xs={6} md={3} key={item.label}>
                  <Box
                    sx={{
                      p: 1.1,
                      borderRadius: 2,
                      height: '100%',
                      border: `1px solid ${isDark ? alpha('#64748b', 0.3) : alpha('#cbd5e1', 0.65)}`,
                      backgroundColor: isDark ? alpha('#0f172a', 0.42) : alpha('#ffffff', 0.78),
                      backdropFilter: 'blur(8px)',
                      transition: 'transform 0.2s ease, border-color 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: alpha(item.color, 0.55),
                      },
                    }}
                  >
                    <Stack direction="row" spacing={0.85} alignItems="center" sx={{ mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 1.25,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: alpha(item.color, isDark ? 0.18 : 0.14),
                          color: item.color,
                        }}
                      >
                        <Icon sx={{ fontSize: 15 }} />
                      </Box>
                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
                        {item.label}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontWeight: 800, color: isDark ? '#f1f5f9' : '#0f172a', fontSize: '0.9rem' }}>
                      {item.value}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <Card elevation={0} sx={modernPanelCardSx}>
                <CardContent sx={{ p: { xs: 1.6, sm: 1.9 } }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
                    <PersonIcon sx={{ color: accent, fontSize: 22 }} />
                    Customer details
                  </Typography>

                  <SummaryRow
                    icon={PersonIcon}
                    label="Customer name"
                    value={booking.customer_name}
                    iconColor={accent}
                    valueLinkColor={accent}
                    isDark={isDark}
                    onValueClick={customerGuestId ? () => navigate(`/customerhub/customers/${customerGuestId}/`) : undefined}
                  />
                  <SummaryRow icon={PhoneIcon} label="Phone" value={booking.customer_phone} iconColor="#38bdf8" isDark={isDark} />
                  <SummaryRow icon={EmailIcon} label="Email" value={booking.customer_email} iconColor="#a78bfa" isDark={isDark} />
                  {customerGuestId ? (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => navigate(`/customerhub/customers/${customerGuestId}/`)}
                      sx={{
                        mt: 1,
                        px: 0,
                        alignSelf: 'flex-start',
                        color: accent,
                        fontWeight: 800,
                        textTransform: 'none',
                      }}
                    >
                      Open in Customer Hub
                    </Button>
                  ) : (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                      No Customer Hub link yet. Customer profiles are managed in Customer Hub.
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Card elevation={0} sx={modernPanelCardSx}>
                <CardContent sx={{ p: { xs: 1.6, sm: 1.9 } }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      color: isDark ? '#f8fafc' : '#0f172a',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <DescriptionOutlinedIcon sx={{ color: accent, fontSize: 22 }} />
                    Documents
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mb: 1.5 }}>
                    Preview voucher and invoice, then download or send to the customer.
                  </Typography>
                  {normalizedBookingId ? (
                    <HallEventDocumentsPreviewPanel
                      bookingId={normalizedBookingId}
                      bookingCode={booking.booking_code || booking.code}
                      guestName={booking.customer_name}
                      isDark={isDark}
                      accent={accent}
                      docLoading={docLoading}
                      setDocLoading={setDocLoading}
                      canSendEmail={canSendInvoiceEmail}
                      canSendWhatsApp={canSendInvoiceWhatsApp}
                      voucherLabel="Voucher"
                      invoiceLabel="Invoice"
                      onNotice={({ message, severity }) => showNotice(severity || 'info', message)}
                    />
                  ) : null}
                </CardContent>
              </Card>

              <Card elevation={0} sx={modernPanelCardSx}>
                <CardContent sx={{ p: { xs: 1.6, sm: 1.9 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.75 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalShippingIcon sx={{ color: accent, fontSize: 22 }} />
                        Vendor Management
                      </Typography>
                      <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mt: 0.4 }}>
                        Assign vendors, track contracts, and record payouts against hall collection.
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        if (isVendorEditingLocked) {
                          showNotice('warning', 'Vendor assignments are locked for completed or cancelled events.');
                          return;
                        }
                        setEditingVendorAssignment(null);
                        setVendorDrawerOpen(true);
                      }}
                      disabled={isVendorEditingLocked}
                      sx={{
                        alignSelf: 'flex-start',
                        color: accent,
                        borderColor: alpha(accent, 0.5),
                        fontWeight: 700,
                        textTransform: 'none',
                      }}
                    >
                      Add vendor
                    </Button>
                  </Box>

                  {isVendorEditingLocked ? (
                    <Alert
                      severity="info"
                      sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        backgroundColor: isDark ? alpha('#0ea5e9', 0.12) : alpha('#e0f2fe', 0.95),
                        color: isDark ? '#bae6fd' : '#0c4a6e',
                      }}
                    >
                      Vendor assignments are locked once an event is completed or cancelled.
                    </Alert>
                  ) : null}

                  <VendorManagementCards
                    vendors={assignedVendors}
                    isDark={isDark}
                    accent={accent}
                    locked={isVendorEditingLocked}
                    removingId={removingVendorId}
                    onAddPayment={(vendor) => {
                      setPayingVendorAssignment(vendor);
                      setVendorPaymentDrawerOpen(true);
                    }}
                    onEdit={(vendor) => {
                      setEditingVendorAssignment(vendor);
                      setVendorDrawerOpen(true);
                    }}
                    onRemove={handleRemoveVendor}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={7}>
              <HallEventBillingSection
                bookingId={normalizedBookingId}
                booking={booking}
                payload={payload}
                isDark={isDark}
                locked={isVendorEditingLocked}
                panelCardSx={modernPanelCardSx}
                onUpdated={handleFinancialUpdated}
                onNotice={showNotice}
                onCollectPayment={() => setPaymentDrawerOpen(true)}
                onAddExtra={() => {
                  setEditingExtraCharge(null);
                  setExtraChargesDrawerOpen(true);
                }}
                onEditExtra={(item) => {
                  setEditingExtraCharge(item);
                  setExtraChargesDrawerOpen(true);
                }}
              />

              {parsedNotes.notes ? (
                <Card elevation={0} sx={modernPanelCardSx}>
                  <CardContent sx={{ p: { xs: 1.6, sm: 1.9 } }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', mb: 1.5 }}>
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={{ color: isDark ? '#cbd5e1' : '#334155', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                      {parsedNotes.notes}
                    </Typography>
                  </CardContent>
                </Card>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <HallAuditDrawer
        open={auditDrawerOpen}
        onClose={() => setAuditDrawerOpen(false)}
        title="Event Audit"
        hallName={booking.event_name}
        hallCode={eventId}
        emptyMessage="No event activity recorded yet."
        events={auditEvents}
        isDark={isDark}
        loading={auditRefreshLoading}
        onRefresh={handleRefreshAuditLogs}
      />

      <HallAddVendorDrawer
        open={vendorDrawerOpen}
        onClose={() => {
          setVendorDrawerOpen(false);
          setEditingVendorAssignment(null);
        }}
        bookingId={normalizedBookingId}
        booking={booking}
        isDark={isDark}
        availableVendors={availableVendors}
        assignedVendors={assignedVendors}
        editingAssignment={editingVendorAssignment}
        onSaved={(result) => {
          handleVendorPayloadUpdated(result);
          showNotice(
            'success',
            editingVendorAssignment ? 'Vendor assignment updated.' : 'Vendor assigned successfully.',
          );
        }}
      />

      <HallAddVendorPaymentDrawer
        open={vendorPaymentDrawerOpen}
        onClose={() => {
          setVendorPaymentDrawerOpen(false);
          setPayingVendorAssignment(null);
        }}
        bookingId={normalizedBookingId}
        booking={booking}
        assignment={payingVendorAssignment}
        isDark={isDark}
        onPaymentAdded={(result) => {
          handleVendorPayloadUpdated(result);
          showNotice('success', 'Vendor payment recorded.');
        }}
      />

      <HallAddPaymentDrawer
        open={paymentDrawerOpen}
        onClose={() => setPaymentDrawerOpen(false)}
        bookingId={normalizedBookingId}
        booking={booking}
        payload={payload}
        isDark={isDark}
        onPaymentAdded={(result) => {
          handleFinancialUpdated(result);
          showNotice('success', 'Payment recorded successfully.');
        }}
      />

      <HallAddExtraChargesDrawer
        open={extraChargesDrawerOpen}
        onClose={() => {
          setExtraChargesDrawerOpen(false);
          setEditingExtraCharge(null);
        }}
        bookingId={normalizedBookingId}
        booking={booking}
        payload={payload}
        isDark={isDark}
        locked={isVendorEditingLocked}
        editingExtra={editingExtraCharge}
        onExtraAdded={(result) => {
          handleFinancialUpdated(result);
          showNotice('success', 'Extra charge added successfully.');
        }}
        onExtraUpdated={(result) => {
          handleFinancialUpdated(result);
          showNotice('success', 'Extra charge updated successfully.');
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
